import React, { useCallback, useRef, useEffect, memo } from "react";
import { EditorContent } from "@tiptap/react";
import { cn } from "@pineseed/gui/util";
import { TagAutocomplete } from "@pineseed/gui/component/tag-autocomplete";
import { useTiptapTextInput } from "@pineseed/workspace/hooks/useTiptapTextInput";
import type { TagSearchResult } from "@pineseed/gui/types";
// import { envConfig } from "../config/env";

interface TiptapRichTextInputProps {
  ref?: React.RefObject<HTMLDivElement>;
  style?: React.CSSProperties;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  height?: number;
  onEditorReady?: (
    getText: () => string,
    getContentWithTags: () => string,
    clearText: () => void,
    hasText: boolean,
    setText: (text: string) => void,
  ) => void;
  onTextChange?: (hasText: boolean) => void;
  portalContainer?: HTMLElement; // Optional container to portal autocomplete to (e.g., document.body)
  onTagCreated?: (tag: TagSearchResult) => void;
}

/**
 * TipTap Rich Text Input Component
 * STRICT ARCHITECTURE COMPLIANCE: Pure UI only
 * - No business logic
 * - No handler imports
 * - Only renders UI based on state from useTiptapTextInput hook
 * - All logic delegated to handler via hook
 * - Optimized for performance with memo and proper isolation
 */
const TiptapRichTextInput = memo(function TiptapRichTextInput({
  ref,
  style,
  onKeyDown,
  placeholder = "Type your message here... Use @ to reference tags",
  className,
  disabled = false,
  height,
  onEditorReady,
  onTextChange,
  portalContainer,
  onTagCreated,
}: TiptapRichTextInputProps) {
  console.log('TiptapRichTextInput Rendered');
  
  const editorRefInternal = ref ?? useRef<HTMLDivElement>(null);

  const {
    editor,
    autocompleteState,
    handleTagSelect,
    handleTagSelectByIndex,
    handleCreateTag,
    stopAutocomplete,
    getText,
    getContentWithTags,
    clearText,
    hasText,
    setText,
  } = useTiptapTextInput();

  // Handle tag selection (pure UI event handling)
  const handleTagSelectWrapper = useCallback(
    (result: TagSearchResult) => {
      const success = handleTagSelect(result);
      if (success) {
        stopAutocomplete();
      }
    },
    [handleTagSelect, stopAutocomplete],
  );

  // Handle tag selection by index for click events
  const handleTagSelectByIndexWrapper = useCallback(
    (index: number) => {
      const success = handleTagSelectByIndex(index);
      if (success) {
        stopAutocomplete();
      }
    },
    [handleTagSelectByIndex, stopAutocomplete],
  );

  // Handle creating new tag (pure UI event handling)
  const handleCreateNewTag = useCallback(
    (name: string) => {
      const result = handleCreateTag(name);
      if (result) {
        handleTagSelectWrapper(result);
        if (onTagCreated) {
          onTagCreated(result);
        }
      }
    },
    [handleCreateTag, handleTagSelectWrapper],
  );

  // Handle external key events
  const handleKeyDownWrapper = useCallback(
    (e: React.KeyboardEvent) => {
      // console.log("🟢 handleKeyDownWrapper called");
      // Call external onKeyDown if provided and not handled by TipTap
      if (onKeyDown && !autocompleteState.isOpen) {
        onKeyDown(e);
      }
    },
    [onKeyDown, autocompleteState.isOpen],
  );

  // Handle container click to focus editor and position cursor at end
  const handleContainerClick = useCallback(() => {
    // console.log("🟢 handleContainerClick called", { editor, disabled });
    if (!editor) return;
    if (disabled) {
      editor.commands.focus();
      return;
    }

    // Focus the editor and position cursor at end using TipTap commands
    editor.commands.focus("end");
  }, [editor, disabled]);

  // Call onEditorReady when editor is available - memoized to prevent unnecessary calls
  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(getText, getContentWithTags, clearText, hasText, setText);
    }
  }, [
    editor,
    onEditorReady,
    getText,
    getContentWithTags,
    clearText,
    hasText,
    setText,
  ]);

  // Call onTextChange when hasText changes - memoized to prevent unnecessary calls
  useEffect(() => {
    if (onTextChange) {
      onTextChange(hasText);
    }
  }, [hasText, onTextChange]);

  // Set up keyboard event delegation - optimized to only run when editor changes
  useEffect(() => {
    if (!editor || !editorRefInternal.current) return;

    const editorElement = editorRefInternal.current.querySelector(
      ".ProseMirror",
    ) as HTMLElement;
    if (!editorElement) return;

    const handleKeyDown = (e: Event) => {
      const keyboardEvent = e as KeyboardEvent;
      // Convert native KeyboardEvent to React KeyboardEvent for compatibility
      const reactEvent = {
        key: keyboardEvent.key,
        code: keyboardEvent.code,
        shiftKey: keyboardEvent.shiftKey,
        ctrlKey: keyboardEvent.ctrlKey,
        metaKey: keyboardEvent.metaKey,
        altKey: keyboardEvent.altKey,
        preventDefault: () => keyboardEvent.preventDefault(),
        stopPropagation: () => keyboardEvent.stopPropagation(),
      } as React.KeyboardEvent;

      handleKeyDownWrapper(reactEvent);
    };

    editorElement.addEventListener("keydown", handleKeyDown);
    return () => {
      editorElement.removeEventListener("keydown", handleKeyDown);
    };
  }, [editor, handleKeyDownWrapper]);

  // Auto-focus on window focus when FOCUS_COMPOSER is enabled
  useEffect(() => {
    if (!import.meta.env.VITE_FOCUS_COMPOSER || !editor || disabled) return;

    const handleWindowFocus = () => {
      // Small delay to ensure editor is ready
      setTimeout(() => {
        editor.commands.focus();
      }, 100);
    };

    window.addEventListener("focus", handleWindowFocus);
    return () => {
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [editor, disabled]);

  if (!editor) {
    return (
      <div
        className={cn(
          "min-h-[1rem] w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm",
          "shadow-sm transition-colors resize-none",
          "flex items-center",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
      >
        <span className="text-muted-foreground">Loading editor...</span>
      </div>
    );
  }

  return (
    <div className={cn("", className)} ref={editorRefInternal} onClick={handleContainerClick}>
      {/* TipTap Editor */}
      <div>
        <EditorContent
          editor={editor}
          className={cn(
            "min-h-[1rem] px-3 py-1 text-sm w-full border border-muted-foreground bg-background",
            "shadow-sm transition-colors resize-none overflow-y-auto cursor-text",
            "focus-within:outline-none focus-within:ring-1 focus-within:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
          disabled={disabled}
          style={{
            ...style,
            height: height,
          }}
        />
      </div>

      {/* Autocomplete dropdown */}
      {autocompleteState.isOpen && (
        <TagAutocomplete
          autocompleteState={autocompleteState}
          onSelect={handleTagSelectWrapper}
          onSelectByIndex={handleTagSelectByIndexWrapper}
          onCreateNew={handleCreateNewTag}
          onClose={stopAutocomplete}
          portalContainer={portalContainer}
        />
      )}

      {/* Custom styles for TipTap editor */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .ProseMirror {
            outline: none !important;
            border: none !important;
            padding: 0 !important;
            margin: 0 !important;
            min-height: 20px;
          }

          .ProseMirror p {
            margin: 0 !important;
            padding: 0 !important;
          }

          .ProseMirror p.is-editor-empty:first-child::before {
            content: "${placeholder}";
            float: left;
            color: hsl(var(--muted-foreground));
            pointer-events: none;
            height: 0;
          }

          .mention-tag {
            background-color: hsl(var(--primary));
            color: hsl(var(--primary-foreground));
            padding: 0.125rem 0.25rem;
            border-radius: 0.25rem;
            font-size: 0.75rem;
            font-weight: 500;
            display: inline-block;
            margin: 0 0.125rem;
            text-decoration: none;
          }

          .mention-tag:hover {
            background-color: hsl(var(--primary) / 0.8);
          }
        `,
        }}
      />
    </div>
  );
});

export { TiptapRichTextInput };
